import { useCallback, useMemo, useState } from 'react';

export type ColumnWindow<T> = {
  columns: T[];
  isAwaitingOlder: boolean;
  showOlder?: () => void;
  showNewer?: () => void;
};

/**
 * Positioned from the newest column, so columns prepended by `loadOlder` do not
 * shift the window. No `loadOlder` means the source is exhausted. A step past the
 * oldest loaded column keeps the window where it is and sets `isAwaitingOlder`
 * until `loadOlder` delivers.
 */
export const useColumnWindow = <T>(
  columns: T[],
  size: number,
  loadOlder?: () => void,
): ColumnWindow<T> => {
  const [hiddenColumns, setHiddenColumns] = useState(0);

  const requestedEnd = columns.length - hiddenColumns;
  const end = Math.max(size, requestedEnd);
  const visible = useMemo(() => columns.slice(end - size, end), [columns, end, size]);

  const showOlder = useCallback(() => {
    const olderColumnsAfterStep = end - 2 * size;
    if (olderColumnsAfterStep < size) loadOlder?.();

    setHiddenColumns(columns.length - end + size);
  }, [columns.length, end, size, loadOlder]);

  const showNewer = useCallback(
    () => setHiddenColumns(Math.max(0, columns.length - end - size)),
    [columns.length, end, size],
  );

  return {
    columns: visible,
    isAwaitingOlder: !!loadOlder && requestedEnd < size,
    showOlder: end > size || loadOlder ? showOlder : undefined,
    showNewer: end < columns.length ? showNewer : undefined,
  };
};
